/**
 * Skeleton Component
 * Loading placeholder component with shimmer animation
 */

import React from "react";
import clsx from "clsx";

export interface SkeletonProps {
  variant?: "text" | "circular" | "rectangular";
  width?: string | number;
  height?: string | number;
  className?: string;
  animation?: "pulse" | "wave" | "none";
}

const Skeleton: React.FC<SkeletonProps> = ({
  variant = "text",
  width,
  height,
  className,
  animation = "pulse",
}) => {
  const variantClasses = {
    text: "rounded",
    circular: "rounded-full",
    rectangular: "rounded-lg",
  };

  const animationClasses = {
    pulse: "animate-pulse",
    wave: "animate-shimmer",
    none: "",
  };

  const style: React.CSSProperties = {};
  if (width) {
    style.width = typeof width === "number" ? `${width}px` : width;
  }
  if (height) {
    style.height = typeof height === "number" ? `${height}px` : height;
  }

  // Default heights for text variant
  if (variant === "text" && !height) {
    style.height = "1em";
  }

  return (
    <div
      className={clsx(
        "bg-gray-200",
        variantClasses[variant],
        animationClasses[animation],
        className,
      )}
      style={style}
    />
  );
};

export default Skeleton;

// Pre-built skeleton patterns
export const TableSkeleton: React.FC<{ rows?: number; columns?: number }> = ({
  rows = 5,
  columns = 4,
}) => {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex gap-4">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Skeleton key={colIndex} className="flex-1" height={20} />
          ))}
        </div>
      ))}
    </div>
  );
};

export const CardSkeleton: React.FC = () => {
  return (
    <div className="bg-white p-6 rounded-lg shadow space-y-3">
      <Skeleton width="60%" height={24} />
      <Skeleton width="100%" height={16} />
      <Skeleton width="80%" height={16} />
      <Skeleton width="40%" height={32} className="mt-4" />
    </div>
  );
};

export const StatCardSkeleton: React.FC = () => {
  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <Skeleton width="50%" height={14} className="mb-2" />
      <Skeleton width="70%" height={32} className="mb-1" />
      <Skeleton width="60%" height={12} />
    </div>
  );
};
